<template>
  <div class="text-start mb-5">
    <h2 class="headings">
      {{ $t('sections.needHelpContactUs') }}
      <p class="line d-block mt-4"></p>
    </h2>
  </div>
  <div class="contact-section">
    <!-- Map Image Section -->
    <div class="map-container">
      <div class="map-view" ref="mapgeoContainer"></div>
    </div>

    <!-- Contact Form -->
    <div class="contact-form-wrapper">
      <form class="contact-form-container" @submit.prevent="handleSubmit">
        <div class="form-grid">
          <!-- First Name -->
          <div class="form-group">
            <basic-label :title="$t('forms.firstName')">
              <BasicInputText
                class="form-input"
                name="firstNameInput"
                v-model="firstName"
                :required="true"
                type="text"
                :hasFocus="false"
              />
            </basic-label>
            <span class="text-danger error-msg">{{ firstNameError }}</span>
          </div>

          <!-- Last Name -->
          <div class="form-group">
            <basic-label :title="$t('forms.lastName') + '*'">
              <BasicInputText
                class="form-input"
                name="lastNameInput"
                v-model="lastName"
                type="text"
                :hasFocus="false"
              />
            </basic-label>
            <span class="text-danger error-msg">{{ lastNameError }}</span>
          </div>

          <!-- Email -->
          <div class="form-group">
            <basic-label :title="$t('forms.emailAddress') + '*'">
              <BasicInputText
                class="form-input"
                name="emailInput"
                v-model="email"
                type="email"
                :hasFocus="false"
                :required="true"
              />
            </basic-label>
            <span class="text-danger error-msg">{{ emailError }}</span>
          </div>

          <!-- Subject -->
          <div class="form-group">
            <basic-label :title="$t('forms.subject')">
              <BasicInputText
                class="form-input"
                name="subjectInput"
                v-model="subject"
                type="text"
                :hasFocus="false"
              />
            </basic-label>
            <span class="text-danger error-msg">{{ subjectError }}</span>
          </div>

          <!-- Message -->
          <div class="form-group full-width">
            <basic-label :title="$t('forms.yourMessageLabel')">
              <textarea
                v-model="textArea"
                class="form-textarea"
                rows="3"
                :required="true"
              ></textarea>
            </basic-label>
            <span class="text-danger error-msg">{{ textAreaError }}</span>
          </div>

          <!-- Submit Button -->
          <div class="form-group full-width text-center">
            <button-filled
              class="btn-submit"
              :size="'large'"
              :hasRoundedCorners="true"
              type="submit"
            >
              <span class="btn-text">{{ $t('forms.sendMessage') }}</span>
            </button-filled>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import ButtonFilled from "../../../atoms/Button/ButtonFilled.vue";
import BasicInputText from "../../../atoms/FormInput/BasicInputText.vue";
import BasicLabel from "../../../atoms/FormInput/BasicLabel.vue";
import validateContact from "../../../molecules/Form/validations/validatorContact.js";
import Mapgeo from "@/plugins/mapgeo";
import { getVendorsService } from "@/services/vendorService";

export default {
  props: {
    markers: {
      type: Array,
      default: () => [],
    },
  },
  components: { ButtonFilled, BasicInputText, BasicLabel },
  setup(props) {
    const formState = validateContact();
    const mapgeoContainer = ref(null);
    let mapgeoInstance = null;
    const fetchedMarkers = ref([]);


    const formatVendorMarkers = (vendors = []) =>
      vendors
        .map((vendor) => {
          const coordinates =
            vendor?.sedeOperativa?.[0]?.loc?.coordinates ||
            vendor?.sedeOperativa?.[0]?.coordinates ||
            vendor?.raw?.sedeOperativa?.[0]?.loc?.coordinates;
          if (!Array.isArray(coordinates) || coordinates.length < 2) {
            return null;
          }
          const label =
            vendor?.denominazioneSociale ||
            vendor?.nome ||
            vendor?.name ||
            vendor?._id ||
            "Vendor";
          const info =
            vendor?.sedeOperativa?.[0]?.value ||
            vendor?.raw?.sedeOperativa?.[0]?.value ||
            "";
          // --- ADD THE VENDOR DATA OBJECT ---
          // Include the fields needed by getVendorRoute logic
          const vendorData = {
            // Pass the fields that getVendorRoute checks
            containerPath: vendor?.containerPath,
            slug: vendor?.slug,
            id: vendor?.id,
            _id: vendor?._id,
            codice: vendor?.codice || vendor?.raw?.codice,
            denominazioneSociale:
              vendor?.denominazioneSociale || vendor?.raw?.denominazioneSociale,
            denominazione: vendor?.denominazione || vendor?.raw?.denominazione,
            name: vendor?.name || vendor?.denominazioneSociale || vendor?.nome,
            nome: vendor?.nome || vendor?.raw?.nome,
          };
          // --- END ADD ---
          return {
            coordinates,
            label,
            info,
            // --- ADD THE VENDOR DATA TO THE RETURNED OBJECT ---
            vendorData: vendorData, // Use a clear name
            // --- END ADD ---
          };
        })
        .filter(Boolean);

    const loadVendorMarkers = async () => {
      if (Array.isArray(props.markers) && props.markers.length) {
        return;
      }

      try {
        const data = await getVendorsService();
        const vendors = data?.venditori || [];
        fetchedMarkers.value = formatVendorMarkers(vendors);
      } catch (error) {
        console.error("Failed to load vendor markers", error);
        fetchedMarkers.value = [];
      }
    };

    const resolvedMarkers = computed(() => {
      if (Array.isArray(props.markers) && props.markers.length) {
        return props.markers;
      }
      return fetchedMarkers.value;
    });

    const renderMap = () => {
      if (!mapgeoContainer.value) return;

      if (mapgeoInstance) {
        mapgeoInstance.destroy();
        mapgeoInstance = null;
      }

      mapgeoInstance = new Mapgeo(mapgeoContainer.value, {
        variant: "italy",
        width: 520,
        height: 700,
        padding: 40,
        fillColor: "#4d4d4d",
        strokeColor: "#979797",
        strokeWidth: 1.1,
        glowColor: "rgba(0, 0, 0, 0.55)",
        markers: resolvedMarkers.value,
      });
    };

    const cleanupMap = () => {
      if (mapgeoInstance) {
        mapgeoInstance.destroy();
        mapgeoInstance = null;
      }
    };

    watch(
      resolvedMarkers,
      () => {
        renderMap();
      },
      { deep: true }
    );

    watch(
      () => mapgeoContainer.value,
      () => {
        renderMap();
      }
    );

    onMounted(() => {
      renderMap();
      loadVendorMarkers();
    });

    onBeforeUnmount(() => {
      cleanupMap();
    });

    return {
      ...formState,
      mapgeoContainer,
    };
  },
  methods: {
    handleSubmit() {
      // Your submit logic here
      console.log('Form submitted');
    }
  }
};
</script>

<style scoped>
  .headings {
    font-size: 2.5rem;
    font-weight: bold;
    position: relative;
    display: inline-block;
  }
  .headings .line {
    display: inline-block;
    height: 3px;
    background-color: black;
    margin: 0 10px;
    vertical-align: middle;
  }

  .contact-section {
    display: flex;
    gap: 40px;
    align-items: center;
    width: 100%;
    padding: 20px 0;
  }

  /* Map Section */
  .map-container {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .map-view {
    width: 100%;
    max-width: 520px;
    aspect-ratio: 13 / 17;
    display: flex;
  }

  .map-view svg {
    width: 100%;
    height: auto;
    display: block;
  }

  /* Form Section */
  .contact-form-wrapper {
    flex: 1;
    display: flex;
    justify-content: center;
  }

  .contact-form-container {
    width: 100%;
    max-width: 600px;
    background-image: url('~@/assets/images/hero-section/slider3.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 30px;
    padding: 50px 40px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    position: relative;
  }

  /* Add overlay for better text readability */
  .contact-form-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.85);
    border-radius: 30px;
    z-index: 0;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
    position: relative;
    z-index: 1;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-group.full-width {
    grid-column: 1 / -1;
  }

  /* Label styling */
  .form-group :deep(label) {
    font-size: 1.05rem;
    font-weight: 500;
    color: #666;
    margin-bottom: 8px;
    display: block;
  }

  /* Input styling */
  .form-input,
  .form-textarea {
    width: 100%;
    padding: 14px 18px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    color: #333;
    outline: none;
    transition: all 0.3s ease;
    box-shadow: none !important;
  }

  .form-input:focus,
  .form-textarea:focus {
    border-color: #333;
    background: #fff;
  }

  .form-textarea {
    resize: vertical;
    min-height: 100px;
    font-family: inherit;
  }

  /* Error message */
  .error-msg {
    font-size: 0.875rem;
    margin-top: 5px;
    display: block;
  }

  /* Submit button */
  .btn-submit {
    background: black;
    color: white;
    padding: 8px 60px;
    border: none;
    border-radius: 12px;
    font-size: 1.05rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-block;
    margin: 10px auto 0;
    width: auto;
    min-width: 200px;
    max-width: 250px;
    text-transform: none;
  }

  .btn-submit:hover {
    background: #333;
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  }

  .btn-submit:active {
    transform: translateY(0);
  }

  .btn-text {
    color: white !important;
    font-size: 1.05rem;
  }

  /* Remove default button-filled styles that might conflict */
  .btn-submit :deep(*) {
    color: white !important;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .contact-section {
      gap: 30px;
    }

    .map-view {
      max-width: 450px;
    }

    .contact-form-container {
      padding: 40px 30px;
    }
  }

  @media (max-width: 768px) {
    .contact-section {
      flex-direction: column;
      gap: 40px;
    }

    .map-container {
      width: 100%;
      order: 2;
    }

    .contact-form-wrapper {
      width: 100%;
      order: 1;
    }

    .contact-form-container {
      padding: 40px 30px;
      border-radius: 20px;
    }

    .form-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }

    .headings {
      font-size: 2rem;
    }

    .btn-submit {
      width: 100%;
      padding: 12px 40px;
    }
  }

  @media (max-width: 480px) {
    .contact-form-container {
      padding: 30px 20px;
    }

    .form-input,
    .form-textarea {
      padding: 12px 15px;
      font-size: 0.95rem;
    }

    .form-group :deep(label) {
      font-size: 1rem;
    }

    .map-view {
      max-width: 100%;
    }
  }
</style>
